import Vue from 'vue'
import moment from 'moment-timezone'

import { curatePhoneNumber } from './helpers'
import { serviceTypes, keyLocations, meetingLocations, keyPickers, countriesList, dialCodeList } from './enums'

const { i18n } = Vue
const translate = (data) => i18n.translate(data)

export const catalogDate = (value) => {
  if (!value) return '---'
  try {
    return moment(value).format('DD/MM/YYYY HH:mm')
  } catch (error) {
    return 'Not found'
  }
}

export const catalogDateWithoutTime = (value) => {
  if (!value) return '—'
  try {
    return moment(value).format('DD/MM/YYYY')
  } catch (error) {
    return 'Not found'
  }
}

export const catalogDateWithoutYear = (value) => {
  if (!value) return '---'
  try {
    return moment(value).format('DD/MM HH:mm')
  } catch (error) {
    return 'Not found'
  }
}

export const formatCountryDate = (date) => {
  if (!date) return '---'
  try {
    const userCountry = localStorage.getItem('user_country') || 'BR'
    const formats = {
      'AU': 'DD/MM/YYYY',
      'BR': 'DD/MM/YYYY',
      'US': 'YYYY-MM-DD',
    }

    const format = formats[userCountry] || 'DD/MM/YYYY'
    const formattedDate = moment.utc(date).format(format)

    return formattedDate
  } catch (error) {
    return '---'
  }
}

export const formatDateWithTimezone = (date) => {
  if (!date) return '---'
  const timezone = moment.tz.guess()
  const formattedDate = moment.tz(date, timezone).format('YYYY-MM-DDTHH:mm:ssZ')
  return formattedDate
}

export const catalogReadableMonthAndYear = (value) => {
  if (!value) return '---'
  try {
    const userCountry = localStorage.getItem('user_country')

    if (userCountry == 'AU') {
      moment.locale('en');
      return captalizeFirstLetter(moment(value).format('MMMM YYYY'));
    }

    moment.locale('pt-br');
    return captalizeFirstLetter(moment(value).format('MMMM [de] YYYY'));
  } catch (error) {
    return 'Not found'
  }
}

export const captalizeFirstLetter = (value) => {
  try {
    return value.charAt(0).toUpperCase() + value.slice(1);
  } catch (error) {
    return 'Not found'
  }
}

export const findCountryFlag = (dialCode) => {
  const country = dialCodeList.find(country => country.dial_code === dialCode)
  return country?.flag
}

export const catalogPhone = (value) => {
  const curatedPhone = curatePhoneNumber(value)
  const flag = findCountryFlag(curatedPhone?.country_code)
  return `${flag ?? ''} ${curatedPhone?.country_code} ${curatedPhone?.phone_number}`
}

export const catalogCpfCnpj = (value) => {
  value = value.replace(/(\.|\/|\-)/g, '')

  if (value.length <= 11) {
    return value.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/g, '$1.$2.$3-$4')
  } else {
    return value.replace(
      /(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/g,
      '$1.$2.$3/$4-$5'
    )
  }
}

export const catalogCnpj = (value) => {
  value = value.replace(/(\.|\/|\-)/g, '')
  return value.replace(
    /(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/g,
    '$1.$2.$3/$4-$5'
  )
}

export const catalogMonetary = (value) => {
  let language
  const currency = localStorage.getItem('user_currency') ?? 'BRL'
  currency === 'BRL' ? language = 'pt-BR' : language = 'en-AU'

  return value.toLocaleString(language, { style: 'currency', currency: currency })
}

export const catalogCurrency = () => {
  const currency = localStorage.getItem('user_currency') ?? 'BRL'
  const countryCurrency = countriesList.find(country => country.currency_code === currency)

  return {
    prefix: countryCurrency.currency_symbol,
    decimal: countryCurrency.currency_decimal,
    thousands: countryCurrency.currency_thousands,
    precision: 2,
    suffix: '',
  }
}

export const cpfCnpj = (value) => {
  if (value) {
    let formattedDocument = null

    const regexCPF = /^(\d{3})(\d{3})?(\d{3})?(\d{2})?/
    const regexCNPJ = /^(\d{2})(\d{3})?(\d{3})?(\d{4})?(\d{2})?/
    const replaceCPF = value.replace(regexCPF, '$1.$2.$3-$4')
    const replaceCNPJ = value.replace(regexCNPJ, '$1.$2.$3/$4-$5')

    value.length <= 11
      ? (formattedDocument = replaceCPF)
      : (formattedDocument = replaceCNPJ)

    return formattedDocument
  }

  return '---'
}

export const zeroPad = (num, places) => String(num).padStart(places, '0')

export const getServiceTypeByName = (name) => {
  const serviceType = serviceTypes.find(
    (serviceType) => serviceType.name.toLowerCase() == name?.toLowerCase()
  )
  return serviceType ? serviceType.value : null
}

export const getMeetingLocationByName = (name) => {
  const meetingLocation = meetingLocations.find(
    (meetingLocation) => meetingLocation.name.toLowerCase() == name?.toLowerCase()
  )
  return meetingLocation ? meetingLocation.value : null
}

export const getKeyPickerByName = (name) => {
  const keyPicker = keyPickers.find((keyPicker) => keyPicker.name.toLowerCase() == name?.toLowerCase())
  return keyPicker ? keyPicker.value : null
}

export const getKeyLocationByName = (name) => {
  const keyLocation = keyLocations.find(
    (keyLocation) => keyLocation.name.toLowerCase() == name?.toLowerCase()
  )
  return keyLocation ? keyLocation.value : null
}

export const whatsApp = (phone) => {
  if (!phone) return '---'

  const whatsAppApi = 'https://api.whatsapp.com/send?phone='
  const parsedPhone = catalogPhone(phone).replace(/\D/g, '')

  return `
    <a href="${whatsAppApi}${parsedPhone}" target="_blank">
      ${phone}
    </a>
  `
}

export const mailto = (mail) => {
  if (!mail) return '---'

  return `<a href="mailto:${mail}" target="_blank">${mail}</a>`
}

export const sortAlphabetically = (data, key) => {
  return data.sort((itemA, itemB) => itemA[key] > itemB[key] ? 1 : -1)
}

export const formatDateForFilters = () => {
  let date = new Date()
  date.setDate(date.getDate() - 45)

  const year = date.getFullYear();

  let month = date.getMonth() + 1;
  month = month < 10 ? '0' + month : month;

  let day = date.getDate();
  day = day < 10 ? '0' + day : day;

  const formattedDate = `${year}-${month}-${day}`

  return formattedDate
}

export const getAttributeNames = (attributeName, language) => {
  language = language.split("-")[0]
  const attributeMappings = {
    en: {
      type: "type",
      realty_code: "realty_code",
      realty_description: "realty_description",
      date: "date",
      time: "hour",
      address: "street",
      additional_address_data: "address_line_2",
      total_area: "square_meters",
      private_area: "private_area_meters",
      observations: "notes",
      external_code: "external_code",
      displacement: "displacement",
      needs_authorization: "needs_authorization",
      meeting_location: "meeting_location",
      meeting_location_address: "meeting_location_address",
      key_location: "key_location",
      key_location_address: "key_location_address",
      key_picker: "key_picker",
      visitor_name: "tenant_name",
      visitor_mail: "tenant_mail",
      visitor_phone: "tenant_phone",
      has_trousseau: "has_trousseau",
      has_furniture: "has_furniture",
      city:"suburb",
      zipCode:"postcode",
      state:"state",
      neighborhood:"neighborhood",
      number:"number",
      visitor_secondary_mail: "tenant_secondary_mail",
      visitor_secondary_phone: "tenant_secondary_phone",
      key_number: "key_number",
      time_slot: "time_slot"
    },
    pt: {
      type: "tipo",
      realty_code: "codigo_do_imovel",
      realty_description: "desc_imovel",
      date: "data",
      time: "horario",
      address: "rua",
      additional_address_data: "complemento",
      square_meters: "metragem_quadrada",
      private_area_meters: "area_privativa",
      observations: "observacoes",
      external_code: "codigo_externo",
      displacement: "deslocamento",
      needs_authorization: "precisa_de_autorizacao",
      meeting_location: "local_de_encontro",
      meeting_location_address: "endereco_de_encontro",
      key_location: "localizacao_da_chave",
      key_location_address: "endereco_da_chave",
      key_picker: "responsavel_pela_chave",
      visitor_name: "nome_do_visitante",
      visitor_mail: "email_do_visitante",
      visitor_phone: "telefone_do_visitante",
      has_trousseau: "tem_enxoval",
      has_furniture: "tem_mobilia",
      city:"cidade",
      zipCode:"cep",
      state:"estado",
      neighborhood:"bairro",
      number:"numero",
      visitor_secondary_mail: "email_secundario",
      visitor_secondary_phone: "telefone_secundario",
      key_number: "numero_da_chave",
      time_slot: "intervalo"
    }
  };
  return attributeMappings[language][attributeName] || attributeName;
}

export const verifyTimePeriod = (scheduledTime) => {
  const time = moment(scheduledTime, 'HH:mm')
  const morningInterval = moment().hour(6).minute(0)
  const afternoonInterval = moment().hour(12).minute(0)

  if (time.isBetween(morningInterval, afternoonInterval)) {
    return 'morning'
  } else {
    return 'afternoon'
  }
}

export const timeSlot = (date, slotSize, timezone) => {
  if (!date) return 'no date provided'

  timezone = timezone ?? moment.tz.guess()
  const startHour = moment(date).tz(timezone).format('HH:mm')
  const formattedDate = moment(date).format('DD/MM/YYYY')

  if (slotSize != 0 && slotSize != undefined) {
    return `${formattedDate} ${startHour} - ${moment(startHour, 'HH:mm').add(slotSize, 'minutes').format('HH:mm')}`
  }

  return `${formattedDate} ${startHour}`
}

export const timeSlotAmPm = (date, slotSize, timezone) => {
  if (!date) return 'no date provided'

  timezone = timezone ?? moment.tz.guess()
  const timezoneDate = moment(date).tz(timezone)

  const startHour = moment(timezoneDate).format('hh:mm A')
  const formattedDate = moment(timezoneDate).format('DD/MM/YYYY')

  if (slotSize != 0 && slotSize != undefined) {
    return `${formattedDate} ${startHour} - ${moment(startHour, 'hh:mm A').add(slotSize, 'minutes').format('hh:mm A')}`
  }

  return `${formattedDate} ${startHour}`
}

export const isArraysEquals = (array1 = [], array2 = []) => {
  if (array1.length !== array2.length) return false

  array1.sort((a, b) => JSON.stringify(a) > JSON.stringify(b) ? 1 : -1)
  array2.sort((a, b) => JSON.stringify(a) > JSON.stringify(b) ? 1 : -1)

  for (let i = 0; i < array1.length; i++) {
    if (JSON.stringify(array1[i]) !== JSON.stringify(array2[i])) {
      return false;
    }
  }

  return true
}

export const dateHourTimezone = (date, time, timezone = moment.tz.guess()) => {
  if (!date || !time) return
  return moment(`${date} ${time}`).tz(timezone).format()
}

export const workerType = (workerType) => {
  return translate(`appointment.workerType.${workerType}`)
}

export const displayContent = (value, format = 'string', fallback = '---',) => {
  if (value === null || value === undefined) return fallback
  if (typeof value === 'string' && value.trim() === '') return fallback
  if (Array.isArray(value) && value.length === 0) return fallback

  if (format === 'date') return catalogDate(value)
  if (format === 'phone') return catalogPhone(value)
  if (format === 'workerType') return workerType(value)

  return value
}